import { useState } from 'react';
import { companyService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import styles from './CompanyModal.module.css';
import { EMAIL_REGEX } from 'utils/email';

// Límites razonables por campo — mismos que valida el backend
// (companiesController.js) para no depender solo de ese lado.
const FIELD_MAX_LENGTH = { name: 150, contact_name: 150, address: 200, url: 255 };

// NIT/teléfono/email — mismo formato que valida el backend
// (utils/validators.js). Los tres son opcionales en Compañías (ver
// Company.js): solo se exige el formato si el campo viene lleno.
const NIT_REGEX   = /^\d{5,15}(-\d)?$/;
const PHONE_REGEX = /^\d{7,10}$/;
// Solo http(s): un `javascript:` se ejecutaría al hacer clic en el enlace (mismo regex que el backend).
const URL_HTTP_REGEX = /^https?:\/\/[^\s]+$/i;

// La API devuelve null en los campos opcionales vacíos (NIT, correo…). Con un null en el formulario, validarCampos hacía form.nit.trim() sobre null,
// lanzaba un TypeError y «Guardar» no hacía nada (sin petición ni aviso): una compañía sin NIT o sin correo no se podía editar.
const sinNulos = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ?? '']));

// Trim antes de exigir "no vacío" — un valor de solo espacios pasaba el
// `required` nativo del input (mismo hallazgo que en Property, ver #159).
function validarCampos(form) {
  if (!form.name.trim()) return 'El nombre es requerido';
  for (const campo of ['name', 'contact_name', 'address', 'url']) {
    if (form[campo].length > FIELD_MAX_LENGTH[campo]) {
      return `El campo "${campo}" no puede tener más de ${FIELD_MAX_LENGTH[campo]} caracteres`;
    }
  }
  if (form.nit.trim()   && !NIT_REGEX.test(form.nit.trim()))     return 'El NIT no es válido — debe ser numérico (ej. 900123456 o 900123456-7)';
  if (form.phone.trim() && !PHONE_REGEX.test(form.phone.trim())) return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) return 'El correo no es válido';
  if (form.url.trim()   && !URL_HTTP_REGEX.test(form.url.trim())) return 'La URL no es válida — debe empezar por http:// o https:// (ej. https://empresa.com)';
  return null;
}

export default function CompanyModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', nit: '', contact_name: '', email: '', phone: '', address: '', url: '',
    ...(editing ? { ...sinNulos(editing), contact_name: editing.contact_name || editing.contact || '' } : {}),
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const campoError = validarCampos(form);
    if (campoError) return setError(campoError);
    setSaving(true); setError('');
    try {
      if (editing) await companyService.update(editing.id, form);
      else         await companyService.create(form);
      onSaved(editing ? 'Compañía actualizada' : 'Compañía creada');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalCompania}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>
              <i className={`fas fa-briefcase ${styles.iconoTitulo}`} />
              {editing ? 'Editar compañía' : 'Nueva compañía'}
            </h3>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={save} noValidate>
          <div className={`modal-body ${styles.cuerpo}`}>
            {error && <AvisoModal>{error}</AvisoModal>}
            <div className={styles.rejilla}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-control" value={form.name}
                  onChange={e => set('name', e.target.value)} required
                  maxLength={FIELD_MAX_LENGTH.name}
                  placeholder="Nombre de la compañía" />
              </div>
              <div className="form-group">
                <label className="form-label">NIT / RUT</label>
                <input className="form-control" value={form.nit}
                  onChange={e => set('nit', e.target.value)}
                  placeholder="Ej: 900123456-1" />
              </div>
            </div>
            <div className={styles.rejilla}>
              <div className="form-group">
                <label className="form-label">Nombre de contacto</label>
                <input className="form-control" value={form.contact_name}
                  onChange={e => set('contact_name', e.target.value)}
                  maxLength={FIELD_MAX_LENGTH.contact_name}
                  placeholder="Persona de contacto" />
              </div>
              <div className="form-group">
                <label className="form-label">Email de contacto</label>
                <input type="email" className="form-control" value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="contacto@empresa.com" />
              </div>
            </div>
            <div className={styles.rejilla}>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-control" value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="Ej: 3001234567" />
              </div>
              <div className="form-group">
                <label className="form-label">URL (opcional)</label>
                <input className="form-control" value={form.url}
                  onChange={e => set('url', e.target.value)}
                  maxLength={FIELD_MAX_LENGTH.url}
                  placeholder="https://empresa.com" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-control" value={form.address}
                onChange={e => set('address', e.target.value)}
                maxLength={FIELD_MAX_LENGTH.address}
                placeholder="Dirección de la compañía" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><i className="fas fa-spinner fa-spin" /> Guardando...</>
                : <><i className="fas fa-save" /> Guardar</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
